/**
 * Abwehrlinie: Filter neben die Leitung stellen, Welle starten, zusehen.
 *
 * Abgespielt wird mit genau der Rechnung, die der Server danach mit den
 * Platzierungen wiederholt – was hier zu sehen ist, ist auch das Ergebnis.
 * Getaktet per setInterval statt requestAnimationFrame, damit es auch in
 * Hintergrund-Tabs weiterläuft.
 */
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  createTdState,
  PACKETS,
  packetPoint,
  placementProblem,
  placeTower,
  startWave,
  stepTd,
  TD,
  TOWERS,
  type PacketKind,
  type TdGame,
  type TdPlacement,
  type TdState,
  type TowerKind,
} from '@hosting-sim/core';
import { Button } from '../ui';
import type { MinigameApi } from './MinigameOverlay';

const CELL = 44;

const TOWER_STYLE: Record<TowerKind, { color: string; icon: string }> = {
  filter: { color: '#f97316', icon: 'U' },
  proxy: { color: '#a855f7', icon: 'S' },
  waf: { color: '#ef4444', icon: 'H' },
  scrubber: { color: '#22d3ee', icon: '★' },
};

function cloneState(state: TdState): TdState {
  return {
    ...state,
    towers: state.towers.map((t) => ({ ...t })),
    packets: state.packets.map((p) => ({ ...p })),
    queue: [...state.queue],
    shots: [...state.shots],
  };
}

export function TowerDefenseGame({ game, api, disabled }: { game: TdGame; api: MinigameApi; disabled: boolean }) {
  const stateRef = useRef<TdState>(createTdState(game));
  const placementsRef = useRef<TdPlacement[][]>(game.waves.map(() => []));
  const [, force] = useState(0);
  const [tool, setTool] = useState<TowerKind>('filter');
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [fast, setFast] = useState(false);
  const [busy, setBusy] = useState(false);
  const state = stateRef.current;
  const rerender = () => force((n) => n + 1);

  // Abspielen – nach der Uhr, nicht nach der Zahl der Timer-Aufrufe: Drosselt
  // der Browser den Takt, werden die verpassten Schritte nachgeholt.
  useEffect(() => {
    if (!state.running) return;
    const msPerStep = 1000 / (TD.STEPS_PER_SECOND * (fast ? 3 : 1));
    let last = performance.now();
    let owed = 0;
    const timer = setInterval(() => {
      const now = performance.now();
      owed += (now - last) / msPerStep;
      last = now;
      let running = true;
      for (; owed >= 1 && running; owed -= 1) running = stepTd(game, stateRef.current);
      rerender();
      if (!running) clearInterval(timer);
    }, msPerStep);
    return () => clearInterval(timer);
  }, [state.running, state.wave, fast]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nach der letzten Welle abgeben.
  const allDone = state.wave >= game.waves.length && !state.running;
  useEffect(() => {
    if (!allDone || disabled) return;
    (async () => {
      setBusy(true);
      const result = await api.submit({ placements: placementsRef.current });
      setBusy(false);
      const share = state.total > 0 ? state.leaked / state.total : 0;
      api.finish(result.ok && share <= TD.WIN_LEAK_SHARE, result.message);
    })();
  }, [allDone]); // eslint-disable-line react-hooks/exhaustive-deps

  function place(x: number, y: number) {
    if (disabled || state.running || allDone) return;
    // Ein Klick auf einen frisch gesetzten Filter dieser Planungsrunde nimmt ihn zurück.
    const thisWave = placementsRef.current[state.wave];
    const own = thisWave.findIndex((p) => p.x === x && p.y === y);
    if (own >= 0) {
      const [removed] = thisWave.splice(own, 1);
      const next = cloneState(state);
      next.towers = next.towers.filter((t) => !(t.x === x && t.y === y));
      next.budget += TOWERS[removed.kind].cost;
      stateRef.current = next;
      setMessage(null);
      rerender();
      return;
    }
    const placement = { x, y, kind: tool };
    const problem = placeTower(game, state, placement);
    if (problem) {
      setMessage(problem);
      return;
    }
    thisWave.push(placement);
    setMessage(null);
    rerender();
  }

  function launch() {
    startWave(game, state);
    setMessage(null);
    rerender();
  }

  const width = game.width * CELL;
  const height = game.height * CELL;
  const nextWave = game.waves[state.wave];
  const counts = (wave: PacketKind[] | undefined) =>
    (['udp', 'syn', 'http'] as PacketKind[]).map((k) => ({ kind: k, count: wave?.filter((p) => p === k).length ?? 0 })).filter((c) => c.count > 0);
  const end = game.path[game.path.length - 1];
  const start = game.path[0];
  const hoverOk = hover && !state.running && !placementProblem(game, state, { ...hover, kind: tool });
  const thisWave = placementsRef.current[state.wave] ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_230px]">
      <div className="min-w-0">
        <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <span>
            Welle <strong>{Math.min(state.wave + (state.running ? 1 : 0), game.waves.length) || 1}</strong>/{game.waves.length}
          </span>
          <span>
            Budget <strong className="text-amber-300">{state.budget}</strong>
          </span>
          <span>
            Abgefangen <strong className="text-emerald-400">{state.blocked}</strong>
          </span>
          <span>
            Durchgekommen{' '}
            <strong className={state.leaked / state.total > TD.WIN_LEAK_SHARE ? 'text-red-400' : 'text-zinc-100'}>{state.leaked}</strong>
            <span className="text-zinc-400"> / erlaubt {Math.floor(state.total * TD.WIN_LEAK_SHARE)}</span>
          </span>
        </div>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full select-none rounded-lg bg-zinc-950"
          onMouseLeave={() => setHover(null)}
          role="img"
          aria-label="Spielfeld"
        >
          {/* Raster */}
          {Array.from({ length: game.width * game.height }, (_, i) => {
            const x = i % game.width;
            const y = Math.floor(i / game.width);
            return (
              <rect
                key={i}
                x={x * CELL + 1}
                y={y * CELL + 1}
                width={CELL - 2}
                height={CELL - 2}
                rx={4}
                fill="#232327"
                onMouseEnter={() => setHover({ x, y })}
                onClick={() => place(x, y)}
                className={clsx(!state.running && 'cursor-pointer')}
              />
            );
          })}
          {/* Leitung */}
          <polyline
            points={game.path.map((c) => `${c.x * CELL + CELL / 2},${c.y * CELL + CELL / 2}`).join(' ')}
            fill="none"
            stroke="#52525b"
            strokeWidth={CELL * 0.7}
            strokeLinejoin="round"
            strokeLinecap="round"
            pointerEvents="none"
          />
          <polyline
            points={game.path.map((c) => `${c.x * CELL + CELL / 2},${c.y * CELL + CELL / 2}`).join(' ')}
            fill="none"
            stroke="#71717a"
            strokeWidth={2}
            strokeDasharray="6 8"
            pointerEvents="none"
          />
          <text x={start.x * CELL + 4} y={start.y * CELL - 4} fontSize={11} fill="#a1a1aa" pointerEvents="none">
            Internet
          </text>
          <rect x={end.x * CELL + 6} y={end.y * CELL + 6} width={CELL - 12} height={CELL - 12} rx={4} fill="#0ea5e9" pointerEvents="none" />
          <text x={end.x * CELL + CELL / 2} y={end.y * CELL + CELL / 2 + 4} textAnchor="middle" fontSize={11} fill="#fff" pointerEvents="none">
            SRV
          </text>

          {/* Reichweite unter dem Mauszeiger */}
          {hover && !state.running && (
            <circle
              cx={hover.x * CELL + CELL / 2}
              cy={hover.y * CELL + CELL / 2}
              r={TOWERS[tool].range * CELL}
              fill={hoverOk ? 'rgba(250,204,21,0.08)' : 'rgba(239,68,68,0.08)'}
              stroke={hoverOk ? '#facc15' : '#ef4444'}
              strokeDasharray="4 4"
              pointerEvents="none"
            />
          )}

          {/* Filter */}
          {state.towers.map((t) => {
            const fresh = thisWave.some((p) => p.x === t.x && p.y === t.y);
            return (
              <g key={`${t.x},${t.y}`} pointerEvents="none">
                <rect
                  x={t.x * CELL + 6}
                  y={t.y * CELL + 6}
                  width={CELL - 12}
                  height={CELL - 12}
                  rx={8}
                  fill="#18181b"
                  stroke={TOWER_STYLE[t.kind].color}
                  strokeWidth={3}
                  strokeDasharray={fresh && !state.running ? '4 3' : undefined}
                />
                <text x={t.x * CELL + CELL / 2} y={t.y * CELL + CELL / 2 + 5} textAnchor="middle" fontSize={15} fontWeight={700} fill={TOWER_STYLE[t.kind].color}>
                  {TOWER_STYLE[t.kind].icon}
                </text>
              </g>
            );
          })}

          {/* Schüsse */}
          {state.shots.map((s, i) => (
            <line
              key={i}
              x1={s.from.x * CELL + CELL / 2}
              y1={s.from.y * CELL + CELL / 2}
              x2={s.to.x * CELL + CELL / 2}
              y2={s.to.y * CELL + CELL / 2}
              stroke={TOWER_STYLE[s.kind].color}
              strokeWidth={3}
              strokeLinecap="round"
              pointerEvents="none"
            />
          ))}

          {/* Pakete */}
          {state.packets.map((p) => {
            const point = packetPoint(game, p.pos);
            const cx = point.x * CELL + CELL / 2;
            const cy = point.y * CELL + CELL / 2;
            return (
              <g key={p.id} pointerEvents="none">
                <circle cx={cx} cy={cy} r={9} fill={PACKETS[p.kind].color} stroke="#09090b" strokeWidth={2} />
                <rect x={cx - 10} y={cy - 16} width={20} height={3} fill="#3f3f46" />
                <rect x={cx - 10} y={cy - 16} width={(20 * Math.max(0, p.hp)) / p.maxHp} height={3} fill="#4ade80" />
              </g>
            );
          })}
        </svg>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {!state.running && !allDone && (
            <Button variant="primary" onClick={launch} disabled={disabled}>
              Welle {state.wave + 1} starten
            </Button>
          )}
          {state.running && (
            <Button size="sm" variant="secondary" onClick={() => setFast((f) => !f)}>
              {fast ? 'Normal' : '3× schneller'}
            </Button>
          )}
          {busy && <span className="text-sm text-zinc-400">Der Server rechnet nach …</span>}
          {message && <span className="text-sm text-red-400">{message}</span>}
          {!state.running && !allDone && !message && (
            <span className="text-xs text-zinc-400">Feld anklicken stellt den gewählten Filter auf. Neu gesetzte lassen sich per Klick zurücknehmen.</span>
          )}
        </div>
      </div>

      <aside className="space-y-3">
        <div className="space-y-1.5">
          {(Object.keys(TOWERS) as TowerKind[]).map((kind) => {
            const spec = TOWERS[kind];
            return (
              <button
                key={kind}
                type="button"
                onClick={() => setTool(kind)}
                className={clsx(
                  'w-full rounded-lg border p-2 text-left transition',
                  tool === kind ? 'border-amber-400 bg-zinc-800' : 'border-zinc-700 hover:bg-zinc-800/60',
                  spec.cost > state.budget && 'opacity-50',
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="font-bold" style={{ color: TOWER_STYLE[kind].color }}>
                    {TOWER_STYLE[kind].icon}
                  </span>
                  <span className="flex-1 text-sm font-medium">{spec.label}</span>
                  <span className="font-mono text-sm text-amber-300">{spec.cost}</span>
                </div>
                <p className="mt-0.5 text-2xs text-zinc-400">{spec.description}</p>
              </button>
            );
          })}
        </div>
        <div className="rounded-lg border border-zinc-700 p-2 text-xs">
          <p className="mb-1 font-medium text-zinc-300">{state.running ? 'Diese Welle' : allDone ? 'Fertig' : `Welle ${state.wave + 1} bringt`}</p>
          {counts(state.running ? game.waves[state.wave] : nextWave).map(({ kind, count }) => (
            <p key={kind} className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full" style={{ background: PACKETS[kind].color }} />
              {count} × {PACKETS[kind].label}
            </p>
          ))}
          <p className="mt-2 text-zinc-500">Jedes abgefangene Paket bringt {TD.KILL_REWARD} Budget.</p>
        </div>
      </aside>
    </div>
  );
}
